/**
 * Abstract chunk functionality
 *
 */

import {
  STATE_APPLIED,
  STATE_CLOSED,
  STATE_CONFLICTED,
  STATE_DELETED,
  STATE_OPEN,
  STATE_PRIVATE,
  STATE_RESOLVED,
  TYPE_REPORT,
  TYPE_RESOURCE,
  TYPE_TO_PARENT_RELATION,
  AGG_KEY,
  DEFAULT_AGG_INDEX
} from './constants'

import {
  cloneDeep,
  extend,
  findIndex,
  forEach,
  get,
  isNumber,
  isObject,
  isString,
  omit,
  set
} from 'lodash'

const REPORT_CHUNK_ID = '__SWARM__.REPORT_CHUNK_ID'
const REPORT_AUTHOR_ID = '__SWARM__.REPORT_AUTHOR_ID'
const REPORT_AUTHOR_NAME = '__SWARM__.REPORT_AUTHOR_NAME'
const REPORT_SCORE = '__SWARM__.REPORT_SCORE'
const REPORT_STATUS = '__SWARM__.REPORT_STATUS'

const CHUNK_DEFAULTS = {
  comments: [],
  scores: {
    crowd: {},
    mine: {}
  },
  type: 'description',
  ext: {},
  startDate: new Date().getTime(),
  dueDate: new Date().getTime(),
  author: {
    id: '888',
    username: 'hyperanon',
    displayName: 'Hyper Anonymous',
    avatar: 'https://api.adorable.io/avatars/hyperanon.png'
  }
}

const OMITTED_PROPS = [
  'comments',
  'scores',
  'author',
  'title',
  'summary',
  'userGroupCode',
  'responseType',
  'crowdRating', // transient prop
  'authorName' // transient prop
]

const CHUNK_TYPE_TITLE = {
  [TYPE_REPORT]: 'Report',
  [TYPE_RESOURCE]: 'Resource'
}
const CHUNK_TYPES = [TYPE_REPORT, TYPE_RESOURCE]

let getChunkTypeTitle = chunkType => {
  return CHUNK_TYPE_TITLE[chunkType]
}

let setReportStatus = (chunks, STATUS) => {
  let status
  if (chunks && chunks.length > 0) {
    let chunk = chunks[0]
    let authorId = get(chunk, 'author.id', null)
    let authorName = get(chunk, 'author.displayName', null)
    let serverAgg = get(chunk, [AGG_KEY, DEFAULT_AGG_INDEX, 'value'], null)
    let crowdNum = get(chunk, [AGG_KEY, DEFAULT_AGG_INDEX, 'count'], null)

    set(window, REPORT_CHUNK_ID, chunk.id)
    set(window, REPORT_AUTHOR_ID, authorId)
    set(window, REPORT_AUTHOR_NAME, authorName)

    set(window, REPORT_SCORE, serverAgg)
    if (!isNumber(serverAgg)) {
      // no rating
      status = STATUS.NO_RATING
      set(window, REPORT_STATUS, status.message)
    } else if (crowdNum === 1) {
      // one rating only
      status = STATUS.ONE_RATING
      set(window, REPORT_STATUS, status.message)
    } else if (crowdNum > 1) {
      if (serverAgg < 50) {
        // more than one ratings and rate < 50
        status = STATUS.NOT_READY
      } else {
        // more than one ratings and rate >= 50
        status = STATUS.READY
      }
      set(window, REPORT_STATUS, status.message)
    }
  } else {
    // no report
    status = STATUS.NO_REPORT
    set(window, REPORT_STATUS, status.message)
  }
  return status
}

let getResponsesParams = ({
  fetchAll,
  offset = 0,
  limit,
  userPreferences,
  type = CHUNK_TYPES.join(','),
  options
}) => {
  const { sortKeys, sortBy } = userPreferences
  let sort
  if (sortBy === undefined) {
    sort = sortKeys[0].field
  } else {
    const sortIndex = findIndex(sortKeys, key => {
      return key.value === sortBy
    })
    sort = sortKeys[sortIndex].field
  }
  let params
  if (fetchAll) {
    params = Object.assign({}, options, {
      params: {
        type,
        sort
      }
    })
  } else {
    params = Object.assign({}, options, {
      params: {
        offset,
        limit,
        type,
        sort
      }
    })
  }
  return params
}

let padChunk = chunk => {
  forEach(cloneDeep(CHUNK_DEFAULTS), function(value, key) {
    if (!(key in chunk)) {
      chunk[key] = value
    }
  })
  return chunk
}

let castrateChunk = chunk => {
  return omit(chunk, OMITTED_PROPS)
}

let canonizeChunk = chunk => {
  let chunky = castrateChunk(chunk)
  if (chunk.content) {
    chunky.content = Buffer.from(chunky.content, 'utf-8').toString('base64')
  }
  return chunky
}

let createChunk = (type, title, content, relation) => {
  // let createdAt = Date.now()
  const isInputObject = typeof type === 'object'
  let chunk = {
    type: !isInputObject ? type : 'text',
    parentRelation: isInputObject
      ? TYPE_TO_PARENT_RELATION[type.type]
      : relation || 'description',
    tags: [],
    state: 'public',
    title: title,
    content: content || '<p>Put your content here ...</p>',
    contentType: 'text/html'
  }
  if (typeof type === 'object') {
    chunk = extend(chunk, type)
  }
  if (chunk.type === 'question') {
    chunk.state = 'open'
    chunk.ext = {
      frozen: true
    }
  }
  return padChunk(chunk)
}

let normalizeContent = content => {
  return Buffer.from(content, 'base64').toString('utf-8')
}

let normalizeChunk = chunk => {
  if (chunk.content) {
    chunk.content = normalizeContent(chunk.content)
  }
  if (chunk.summary) {
    chunk.summary = normalizeContent(chunk.summary)
  }
  try {
    if (isString(chunk.ext) && chunk.ext.length > 0) {
      chunk.ext = JSON.parse(chunk.ext)
    } else if (!isObject(chunk.ext)) {
      chunk.ext = {}
    }
  } catch (e) {
    chunk.ext = {}
    // pass
  }
  chunk.authorName = chunk.author && chunk.author.displayName
  return padChunk(chunk)
}

let canDisplay = (chunk, currentUser) => {
  return (
    !isDeleted(chunk) && (!isPrivate(chunk) || isAuthoredBy(chunk, currentUser))
  )
}

let isOpen = chunk => chunk.state === STATE_OPEN
let isPrivate = chunk => chunk.state === STATE_PRIVATE
let isPublic = chunk => chunk.state !== STATE_PRIVATE
let isClosed = chunk => chunk.state === STATE_CLOSED
let isDeleted = chunk => chunk.state === STATE_DELETED
let isApplied = chunk => chunk.state === STATE_APPLIED
let isResolved = chunk => chunk.state === STATE_RESOLVED
let isNotResolved = chunk => chunk.state !== STATE_RESOLVED
let isInConflict = chunk => chunk.state === STATE_CONFLICTED
let isReport = chunk => chunk.type === TYPE_REPORT
let isResource = chunk => chunk.type === TYPE_RESOURCE

let isAuthoredBy = (chunk, user) => {
  if (!user) {
    return false
  }
  return chunk.author.id === user.id
}

export default {
  canDisplay,
  canonizeChunk,
  castrateChunk,
  createChunk,
  getChunkTypeTitle,
  getResponsesParams,
  isApplied,
  isAuthoredBy,
  isClosed,
  isDeleted,
  isInConflict,
  isNotResolved,
  isOpen,
  isPrivate,
  isPublic,
  isReport,
  isResolved,
  isResource,
  normalizeChunk,
  padChunk,
  setReportStatus
}
